import { useEffect } from 'react';

interface IUseClickEscOptions {
  /**
   * Claims the Escape press for this layer so layers behind it ignore the same
   * keypress. Use it for transient layers (a dropdown inside a modal) that must
   * close on their own without also dismissing whatever is behind them.
   */
  claim?: boolean;
}

/**
 * Listener registration order follows mount order, so a dropdown mounted inside
 * a modal is called *after* the modal. Deferring the non-claiming handlers to a
 * microtask lets a claiming layer register its claim first, regardless of which
 * listener the browser invoked first.
 */
const claimedEvents = new WeakSet<KeyboardEvent>();

const isEscape = (ev: KeyboardEvent): boolean => {
  const key = ev.key || ev.keyCode;
  return key === 'Escape' || key === 'Esc' || key === 27;
};

export function useClickEsc(
  handler: () => void,
  active = true,
  { claim = false }: IUseClickEscOptions = {}
): void {
  useEffect(() => {
    const maybeHandler = (ev: KeyboardEvent): void => {
      if (!active || !isEscape(ev)) return;

      if (claim) {
        claimedEvents.add(ev);
        handler();
        return;
      }

      Promise.resolve().then(() => {
        if (!claimedEvents.has(ev)) handler();
      });
    };

    window.addEventListener('keyup', maybeHandler);

    return () => window.removeEventListener('keyup', maybeHandler);
  }, [handler, active, claim]);
}
